import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Map, { NavigationControl, ViewState } from 'react-map-gl';
import {Button, ButtonGroup} from '@progress/kendo-react-buttons';
import { TextBox } from '@progress/kendo-react-inputs';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { plusIcon, xIcon } from '@progress/kendo-svg-icons';
import { Geometry, Point, Feature, Polygon } from 'geojson';
import classNames from 'classnames/bind';

import { useAppSelector } from '../../../store/hooks';
import { selectAppSettings } from '../../../features';
import { DrawControl } from '../map';
import { IPosition } from '../../../model';
import { geoUtils } from '../../../utils';

import styles from './CoordinatesMapEdit.module.scss';
import MapboxDraw from "@mapbox/mapbox-gl-draw";

const cx = classNames.bind(styles);

export enum DrawGeometryType {
  Point,
  Polygon,
}

interface ICoordinatesEditProps {
  position: IPosition;
  onChange: (position: IPosition) => void;
  className?: string;
}

const CoordinatesEdit: FC<ICoordinatesEditProps> = ({ position, onChange, className }) => {
  const [coordinates, setCoordinates] = useState<IPosition>(position);

  useEffect(() => {
    setCoordinates(position);
  }, [position]);

  const onLongitudeChange = (value: number) => {
    const position = { ...coordinates, longitude: value };
    setCoordinates(position);
    onChange(position);
  };

  const onLatitideChange = (value: number) => {
    const position = { ...coordinates, latitude: value };
    setCoordinates(position);
    onChange(position);
  };

  return (
    <div className={className}>
      <label htmlFor='longitiude'>X</label>
      <TextBox
        id='longitiude'
        className={styles.coordinatesText}
        size='small'
        required={true}
        valid={geoUtils.validateLongitude(coordinates.longitude)}
        value={coordinates.longitude}
        onChange={(e) => onLongitudeChange(parseFloat(e.value as string))}
        type='number'
      />
      <label htmlFor='latitiude'>Y</label>
      <TextBox
        id='latitiude'
        className={styles.coordinatesText}
        size='small'
        required={true}
        valid={geoUtils.validateLatitude(coordinates.latitude)}
        value={coordinates.latitude}
        onChange={(e) => onLatitideChange(parseFloat(e.value as string))}
        type='number'
      />
    </div>
  );
};

const geometryToPositions = (geometry?: Geometry): IPosition[] => {
  if (!geometry) {
    return [];
  }

  if (geometry?.type === 'Point') {
    const p = (geometry as Point).coordinates;
    return [{ longitude: geoUtils.round(p[0]), latitude: geoUtils.round(p[1]) }];
  } else if (geometry?.type === 'Polygon') {
    const p = (geometry as Polygon).coordinates[0];

    if (p.length > 3 &&
        p[0][0] === p[p.length - 1][0] &&
        p[0][1] === p[p.length - 1][1]) {
      return p.slice(0, -1).map((p) => ({ longitude: geoUtils.round(p[0]), latitude: geoUtils.round(p[1]) }));
    }

    return p.map((p) => ({ longitude: geoUtils.round(p[0]), latitude: geoUtils.round(p[1]) }));
  } else {
    throw new Error(`Unsupported geometry type ${geometry.type}`);
  }
};

const positionsToGeometry = (positions: IPosition[], type: DrawGeometryType): Geometry | null => {
  if (!positions?.length) {
    return null;
  }

  if (type === DrawGeometryType.Point) {
    return {
      type: 'Point',
      coordinates: [positions[0].longitude, positions[0].latitude],
    };
  } else if (type === DrawGeometryType.Polygon) {

    const coordinates = positions.map((p) => [p.longitude, p.latitude]);

    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];

    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push(first);
    }

    return {
      type: 'Polygon',
      coordinates: [coordinates],
    };
  } else {
    throw new Error(`Unsupported geometry type ${type}`);
  }
};

const createViewState = (longitude: number, latitude: number): ViewState => ({
  longitude: longitude,
  latitude: latitude,
  zoom: 16,
  bearing: 0,
  pitch: 0,
  padding: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
});

const drawStyles = [
  // POINT
  {
    id: 'points',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#D13438',
    },
  },
  // ACTIVE (being drawn)
  // line stroke
  {
    id: 'gl-draw-line',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#D13438',
      'line-width': 2,
    },
  },
  // polygon fill
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: {
      'fill-color': '#D13438',
      'fill-outline-color': '#D13438',
      'fill-opacity': 0.3,
    },
  },
  // polygon mid points
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 3,
      'circle-color': '#fbb03b',
    },
  },
  // polygon outline stroke
  // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#D13438',
      'line-width': 2,
    },
  },
  // vertex point halos
  {
    id: 'gl-draw-polygon-and-line-vertex-halo-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#FFF',
    },
  },
  // vertex points
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: {
      'circle-radius': 3,
      'circle-color': '#D20C0C',
    },
  },
];

interface ICoordinatesMapEditProps extends FieldRenderProps {
  geometryType: DrawGeometryType;
  center: Point;
}

export const CoordinatesMapEdit: FC<ICoordinatesMapEditProps> = (props: ICoordinatesMapEditProps) => {
  const { geometryType, center, value, onChange } = props;
  const geometry = value as Geometry | undefined;

  const settings = useAppSelector(selectAppSettings);

  const drawRef = useRef<MapboxDraw>();
  const [viewState, setViewState] = useState<ViewState | null>(null);
  const [coordinates, setCoordinates] = useState<IPosition[]>(geometryToPositions(geometry));
  const [currentGeometryType, setCurrentGeometryType] = useState<DrawGeometryType>(geometryType);

  useEffect(() => {
    const pos = geometryToPositions(geometry);
    setCoordinates(pos);

    if (!drawRef.current) return;

    const currentFeatures = drawRef.current?.getAll().features;

    if (geometry) {
      const mapGeometry = currentFeatures?.length > 0 ? currentFeatures[0].geometry : null;
      const isSame = JSON.stringify(mapGeometry) === JSON.stringify(geometry);

      if (!isSame) {
        drawRef.current?.deleteAll();
        drawRef.current?.add(geometry);
      }
    } else if (currentFeatures?.length > 0) {
      drawRef.current?.deleteAll();
    }
  }, [geometry]);

  useEffect(() => {
    const viewState = createViewState(center.coordinates[0], center.coordinates[1]);
    setViewState(viewState);
  }, [center]);

  const coordinatesValid = useMemo(() => {
    const positionsValid = coordinates.every((c) => geoUtils.validatePosition(c));

    if (!positionsValid) return false;

    if (currentGeometryType === DrawGeometryType.Point) {
      return coordinates.length === 1;
    }

    if (currentGeometryType === DrawGeometryType.Polygon) {
      return coordinates.length >= 3;
    }

    return false;
  }, [coordinates, currentGeometryType, geometryType]);

  const onDrawUpdate = (evt: { features: object[] }) => {
    if (evt.features.length) {
      const geometry = (evt.features[0] as Feature).geometry as Geometry;

      if (geometry.type === 'Point') setCurrentGeometryType(DrawGeometryType.Point);
      if (geometry.type === 'Polygon') setCurrentGeometryType(DrawGeometryType.Polygon);

      onChange({ value: geometry });
      setCoordinates(geometryToPositions(geometry));
    }
  };

  const onDrawDelete = () => {
    onChange({ value: undefined });
    setCoordinates([]);
  };

  const onDrawModeChange = (evt: { mode: string }) => {
    if (evt.mode === 'draw_polygon' || evt.mode === 'draw_point') {
      const all = drawRef.current?.getAll();

      if (all && all.features.length > 1) {
        const currentDrawingId = all.features[all.features.length - 1].id;

        const idsToDelete = all.features
            .map(f => f.id)
            .filter(id => id !== undefined && id !== currentDrawingId) as string[];

        if (idsToDelete.length > 0) {
          drawRef.current?.delete(idsToDelete);
        }
      }
    }
  };

  const onCoordinatesChange = (index: number, value: IPosition) => {
    setCoordinates((coordinates) => coordinates.map((c, i) => (i === index ? value : c)));
  };


  const onAddButtonClick = () => {
    setCoordinates((prev) => {
      const lastPoint = prev.length > 0 ? prev[prev.length - 1] : { longitude: 0, latitude: 0 };
      return [...prev, lastPoint];
    });
  };

  const onDeleteCoordinatesClick = (index: number) => {
    setCoordinates((coordinates) => coordinates.filter((_, i) => i !== index));
  };

  const onApplyChangesClick = () => {
    const geometry = positionsToGeometry(coordinates, currentGeometryType);

    if (geometry) {
      drawRef.current?.deleteAll();
      drawRef.current?.add(geometry);
      drawRef.current?.changeMode('simple_select');
      onChange({ value: geometry });

      if (coordinates.length > 0) {
        let targetLong = 0;
        let targetLat = 0;

        if (currentGeometryType === DrawGeometryType.Point) {
          targetLong = coordinates[0].longitude;
          targetLat = coordinates[0].latitude;
        } else if (currentGeometryType === DrawGeometryType.Polygon) {
          const sum = coordinates.reduce(
              (acc, curr) => ({
                longitude: acc.longitude + curr.longitude,
                latitude: acc.latitude + curr.latitude,
              }),
              { longitude: 0, latitude: 0 }
          );

          targetLong = sum.longitude / coordinates.length;
          targetLat = sum.latitude / coordinates.length;
        }

        setViewState(createViewState(targetLong, targetLat));
      }
    }
  };

  const onGeometryTypeChange = (newType: DrawGeometryType) => {
    const mode = newType === DrawGeometryType.Point ? 'draw_point' : 'draw_polygon';

    if (newType === currentGeometryType) {
      drawRef.current?.changeMode(mode);
      return;
    }

    setCurrentGeometryType(newType);

    drawRef.current?.deleteAll();
    setCoordinates([]);
    onChange({ value: undefined });

    setTimeout(() => {
      drawRef.current?.changeMode(mode);
    }, 0);
  };

  return (
    <div className={styles.columns}>
      <div className={styles.map}>
        <Map
          {...viewState}
          maxZoom={18}
          mapboxAccessToken={settings?.map.mapbox_api_token}
          mapStyle={settings?.map.style}
          onMove={(ev) => setViewState(ev.viewState)}
          onLoad={() => {
            if (geometry) drawRef.current?.add(geometry);
          }}
        >
          <NavigationControl position='top-right' showCompass={false} />
          <DrawControl
              key={currentGeometryType}
            ref={drawRef}
            styles={drawStyles}
            position='bottom-right'
            displayControlsDefault={false}
            controls={{
              polygon: currentGeometryType === DrawGeometryType.Polygon,
              point: currentGeometryType === DrawGeometryType.Point,
              trash: true,
            }}
            defaultMode='simple_select'
            onCreate={onDrawUpdate}
            onUpdate={onDrawUpdate}
            onDelete={onDrawDelete}
            onModeChange={onDrawModeChange}
          />
        </Map>
      </div>

      <div className={styles.coordinates}>
        <div className={styles.typeToggle} style={{marginBottom: '10px'}}>
          <ButtonGroup>
            <Button
                type={'button'}
                togglable={true}
                selected={currentGeometryType === DrawGeometryType.Point}
                onClick={() => onGeometryTypeChange(DrawGeometryType.Point)}
            >
              Point
            </Button>
            <Button
                type={'button'}
                togglable={true}
                selected={currentGeometryType === DrawGeometryType.Polygon}
                onClick={() => onGeometryTypeChange(DrawGeometryType.Polygon)}
            >
              Polygon
            </Button>
          </ButtonGroup>
        </div>
        <div className={cx('row', 'spaceBetween')}>
          <div>Coordinates</div>
          {currentGeometryType === DrawGeometryType.Polygon && (
            <Button type='button' className={styles.iconButton} svgIcon={plusIcon} size='small' onClick={onAddButtonClick} />
          )}
        </div>
        {coordinates.map((c, idx) => (
          <div key={idx} className={cx('row', { rowCoordinatesBorder: idx !== coordinates.length - 1 })}>
            <div>{currentGeometryType === DrawGeometryType.Polygon ? `Point ${idx + 1}` : 'Point'}</div>
            <CoordinatesEdit position={c} onChange={(c) => onCoordinatesChange(idx, c)} className={styles.coordinatesControl} />
            {currentGeometryType === DrawGeometryType.Polygon && (
              <Button
                type='button'
                className={styles.iconButton}
                svgIcon={xIcon}
                size='small'
                onClick={() => onDeleteCoordinatesClick(idx)}
              />
            )}
          </div>
        ))}

        <Button type='button' size='small' style={{ width: '100%' }} onClick={onApplyChangesClick} disabled={!coordinatesValid}>
          Apply Changes
        </Button>
      </div>
    </div>
  );
};
